import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, Modal, EmptyState, LoadingSkeleton, FilterBar, FilterSelect } from '../ui';
import { DocumentTextIcon, ArrowDownTrayIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../../utils/roles';
import { safeErrorMessage } from '../../utils/apiError';
import useApi from '../portal/useApi';
import { termLabel } from '../portal/format';
import { downloadFile } from '../portal/download';
import { STATUS } from './ReportView';
import portal from '../portal/Portal.module.css';
import styles from './Reports.module.css';

// Mirrors backend/report_cards.py's OVERSIGHT_ROLES
const OVERSIGHT_ROLES = ['administrator', 'principal', 'head_of_academics'];
const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const BULK = {
  submit: { label: 'Submit for finalisation', from: 'draft', verb: 'submitted for finalisation', note: 'Teachers will no longer be able to change these reports unless they are returned.' },
  return: { label: 'Return to teacher', from: 'submitted', verb: 'returned to the class teacher', note: 'They go back to draft so the teacher can change them.' },
  publish: { label: 'Publish', from: 'submitted', verb: 'published', note: 'Each report is frozen as it stands and becomes visible to the student\'s parents.' },
  unpublish: { label: 'Unpublish', from: 'published', verb: 'unpublished', note: 'Parents lose sight of these reports and of this term\'s marks until they are published again.' },
};

// Term report cards for a class or a whole Form: generate, review, and move
// them from draft to submitted (class teacher) to published (Head of
// Academics). The backend decides who may do what; buttons here only hide
// what a role can never do.
function ReportCards({ token, user }) {
  const navigate = useNavigate();
  const oversight = hasAnyRole(user, OVERSIGHT_ROLES);
  const terms = useApi(token, '/api/v1/terms');
  const classes = useApi(token, '/api/v1/classes');
  const forms = useApi(token, '/api/v1/reports/cards/forms');

  const [termId, setTermId] = useState('');
  const [scope, setScope] = useState('class');
  const [classId, setClassId] = useState('');
  const [form, setForm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  // Default to the current term once terms arrive
  useEffect(() => {
    if (termId || !terms.data?.length) return;
    setTermId(String((terms.data.find((term) => term.is_current) || terms.data[0]).id));
  }, [terms.data, termId]);

  const target = scope === 'class' ? (classId ? { class_id: Number(classId) } : null) : (form ? { form } : null);
  const ready = Boolean(termId && target);
  const query = ready ? new URLSearchParams({ term_id: termId, ...(scope === 'class' ? { class_id: classId } : { form }) }).toString() : '';
  const cards = useApi(token, ready ? `/api/v1/reports/cards?${query}` : null);

  const all = cards.data || [];
  const rows = all.filter((row) => !statusFilter || row.status === statusFilter);
  const count = (status) => all.filter((row) => row.status === status).length;
  const targetName = scope === 'class' ? (classes.data || []).find((item) => String(item.id) === classId)?.display_name : form;
  // A teacher only ever sees their own classes here, so "can submit" is
  // settled by the backend; oversight roles get every action.
  const actions = oversight ? ['submit', 'publish', 'return', 'unpublish'] : ['submit'];

  const post = async (path, body) => fetch(`/api/v1/reports/cards/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });

  const handleGenerate = async () => {
    setWorking('generate');
    setError('');
    setNotice(null);
    try {
      const response = await post('generate', { term_id: Number(termId), ...target });
      if (response.ok) {
        const data = await response.json();
        setNotice({
          text: data.created > 0
            ? `${data.created} ${data.created === 1 ? 'report' : 'reports'} generated for ${data.classes.join(', ')}.${data.already_existed ? ` ${data.already_existed} already existed.` : ''}`
            : `Nothing new to generate: all ${data.already_existed} students already have a report.`,
          list: data.without_marks.length ? { title: 'These students have no marks recorded yet for this term:', items: data.without_marks } : null,
        });
        cards.reload();
      } else {
        setError(await safeErrorMessage(response, 'Failed to generate reports'));
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setWorking('');
    }
  };

  const handleBulk = async () => {
    const action = confirming;
    setWorking(action);
    setError('');
    setNotice(null);
    try {
      const ids = all.filter((row) => row.status === BULK[action].from).map((row) => row.id);
      const response = await post(action, { ids });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setNotice({
          text: `${data.changed} ${data.changed === 1 ? 'report' : 'reports'} ${BULK[action].verb}.`,
          list: data.skipped?.length ? { title: 'Not changed:', items: data.skipped.map((item) => `${item.student_name}: ${item.reason}`) } : null,
        });
        cards.reload();
      } else {
        setError(data.error || 'That could not be done.');
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setWorking('');
      setConfirming(null);
    }
  };

  const handleClassPdf = async () => {
    setWorking('pdf');
    setError(await downloadFile(token, `/api/v1/reports/cards/pdf?${query}`, 'reports.pdf'));
    setWorking('');
  };

  const handleRowPdf = async (row) => {
    setError(await downloadFile(token, `/api/v1/reports/cards/${row.id}/pdf`, 'report.pdf'));
  };

  const columns = [
    { header: 'Pos.', width: '4rem', render: (row) => (row.position ? `${row.position}` : '—') },
    { header: 'Student', render: (row) => <strong>{row.student_name}</strong> },
    ...(scope === 'form' ? [{ header: 'Class', accessor: 'class_name' }] : []),
    { header: 'Average', render: (row) => (row.average != null ? `${row.average}%` : '—') },
    { header: 'Subjects', render: (row) => row.subjects_count || <Badge variant="warning">No marks</Badge> },
    {
      header: 'Comments',
      render: (row) => (row.has_class_teacher_comment && row.has_head_comment ? 'Both'
        : row.has_class_teacher_comment ? 'Class teacher' : row.has_head_comment ? 'Head' : <span className={portal.muted}>None yet</span>),
    },
    { header: 'Status', render: (row) => <Badge variant={STATUS[row.status]?.badge || 'default'}>{STATUS[row.status]?.label || row.status}</Badge> },
    {
      header: '',
      render: (row) => (
        <div className={styles.actions}>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/report-cards/${row.id}`)}>Review</Button>
          <Button variant="ghost" size="sm" onClick={() => handleRowPdf(row)} aria-label={`Download PDF for ${row.student_name}`}>
            <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>Report Cards</h2>
          <p className={portal.subtitle}>
            Generate term reports for a class or Form, review them, then {oversight ? 'publish them to parents' : 'submit them to the Head of Academics for publishing'}.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && (
        <div className="alert alert-success">
          {notice.text}
          {notice.list && (<><div style={{ marginTop: '0.5rem' }}>{notice.list.title}</div><ul className={styles.skipped}>{notice.list.items.map((item) => <li key={item}>{item}</li>)}</ul></>)}
        </div>
      )}

      <Card>
        {oversight && (
          <div className={styles.scopeRow}>
            <span className={portal.muted}>Reports for:</span>
            <label><input type="radio" name="report-scope" checked={scope === 'class'} onChange={() => setScope('class')} /> One class</label>
            <label><input type="radio" name="report-scope" checked={scope === 'form'} onChange={() => setScope('form')} /> A whole Form (all its classes)</label>
          </div>
        )}
        <FilterBar>
          <FilterSelect label="Term" value={termId} onChange={setTermId} options={(terms.data || []).map((term) => ({ value: String(term.id), label: termLabel(term) }))} allLabel="Choose a term" />
          {scope === 'class' ? (
            <FilterSelect label="Class" value={classId} onChange={setClassId} options={(classes.data || []).map((item) => ({ value: String(item.id), label: item.display_name }))} allLabel="Choose a class" />
          ) : (
            <FilterSelect label="Form" value={form} onChange={setForm} options={forms.data || []} allLabel="Choose a Form" />
          )}
          <Button variant="primary" size="sm" onClick={handleGenerate} disabled={!ready || Boolean(working)}>
            <SparklesIcon style={{ width: 16, height: 16 }} />
            {working === 'generate' ? 'Generating…' : 'Generate reports'}
          </Button>
        </FilterBar>
        <p className={styles.hint} style={{ margin: 0 }}>
          Generating creates a draft report for every student in the {scope === 'class' ? 'class' : 'Form'} who does not have one yet. It is safe to run again after adding students.
        </p>
      </Card>

      {ready && (
        <div className={styles.step}>
          <div className={portal.statsOnPage}>
            <div className={portal.stat}><div className={portal.statLabel}>Reports</div><div className={portal.statValue}>{all.length}</div></div>
            <div className={portal.stat}><div className={portal.statLabel}>Draft</div><div className={portal.statValue}>{count('draft')}</div></div>
            <div className={portal.stat}><div className={portal.statLabel}>Submitted</div><div className={portal.statValue}>{count('submitted')}</div></div>
            <div className={portal.stat}><div className={portal.statLabel}>Published</div><div className={`${portal.statValue} ${count('published') ? portal.statGood : ''}`}>{count('published')}</div></div>
          </div>

          <FilterBar shown={rows.length} total={all.length} onClear={statusFilter ? () => setStatusFilter('') : undefined}>
            <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={Object.entries(STATUS).map(([value, item]) => ({ value, label: item.label }))} allLabel="Any status" />
            {actions.map((action) => (
              <Button key={action} variant={action === 'publish' || (action === 'submit' && !oversight) ? 'primary' : 'secondary'} size="sm" onClick={() => setConfirming(action)} disabled={count(BULK[action].from) === 0 || Boolean(working)}>
                {BULK[action].label} ({count(BULK[action].from)})
              </Button>
            ))}
            <Button variant="secondary" size="sm" onClick={handleClassPdf} disabled={all.length === 0 || Boolean(working)}>
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              {working === 'pdf' ? 'Preparing…' : `Download all as PDF`}
            </Button>
          </FilterBar>

          <Card>
            {cards.loading && !cards.data ? (
              <LoadingSkeleton type="text" rows={6} />
            ) : cards.error ? (
              <div className="alert alert-error">{cards.error}</div>
            ) : all.length === 0 ? (
              <EmptyState title="No reports yet" message={`Press "Generate reports" to create them for ${targetName || 'this selection'}.`} icon={<DocumentTextIcon />} />
            ) : (
              <Table columns={columns} data={rows} emptyMessage="No reports with that status." />
            )}
          </Card>
        </div>
      )}

      <Modal isOpen={Boolean(confirming)} onClose={() => setConfirming(null)} title={confirming ? BULK[confirming].label : ''}>
        {confirming && (
          <div>
            <p className={styles.hint}>
              <strong>{count(BULK[confirming].from)} {count(BULK[confirming].from) === 1 ? 'report' : 'reports'}</strong> for {targetName} will be {BULK[confirming].verb}. {BULK[confirming].note}
            </p>
            <div className={styles.actions}>
              <Button variant="primary" onClick={handleBulk} disabled={Boolean(working)}>{working ? 'Working…' : BULK[confirming].label}</Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ReportCards;
